import { EnableDevBoxAddOnContract } from '../../../data/contracts/add-ons';
import { DevBoxAddOn } from '../../../models/add-ons';
import { UnionValueMap } from '../../../types/union-map';
import {
    ActionName,
    ActionType,
    ErrorPayload,
    FailedPayload,
    IndexedPayload,
    OptionalResultPayload,
    ResultPayload,
} from '../core-actions';
import {
    createOrReplaceDevBoxAddOn,
    enableDevBoxAddOn,
    enableDevBoxAddOnAccepted,
    enableDevBoxAddOnWithPolling,
    enableDevBoxTunnel,
    enableDevBoxTunnelError,
    enableDevBoxTunnelFailed,
    enableDevBoxTunnelPhaseStartDevBox,
    enableDevBoxTunnelPhaseTunnelEnabling,
    enableDevBoxTunnelPhaseTunnelEnablingFinished,
    enableDevBoxTunnelPhaseTunnelNotEnabled,
    enableDevBoxTunnelSuccess,
    getDevBoxAddOn,
    listDevBoxAddOns,
    loadDevBoxAddOnsForDevBoxes,
    pollNonTerminalDevBoxAddOn,
} from './add-ons-action-creators';

type DevBoxAddOnsActionName =
    | ActionName<'PollNonTerminalDevBoxAddOn', 'Complete' | 'Error'>
    | ActionName<'ListDevBoxAddOns', 'Error' | 'Failed' | 'Success'>
    | ActionName<'LoadDevBoxAddOnsForDevBoxes', 'Error' | 'Failed' | 'Success'>
    | ActionName<'GetDevBoxAddOn', 'Error' | 'Failed' | 'Success'>
    | ActionName<'EnableDevBoxAddOn', 'Accepted' | 'Error' | 'Failed' | 'Success'>
    | ActionName<'EnableDevBoxAddOnWithPolling', 'Error' | 'Failed' | 'Success'>
    | ActionName<'CreateOrReplaceDevBoxAddOn', 'Error' | 'Failed' | 'Success'>
    | ActionName<'EnableDevBoxTunnel', 'Error' | 'Failed' | 'Success'>
    | ActionName<'EnableDevBoxTunnelPhaseStartDevBox', never, false>
    | ActionName<'EnableDevBoxTunnelPhaseTunnelEnabling', never, false>
    | ActionName<'EnableDevBoxTunnelPhaseTunnelEnablingFinished', never, false>
    | ActionName<'EnableDevBoxTunnelPhaseTunnelNotEnabled', never, false>;

export enum DevBoxAddOnsActionType {
    ListDevBoxAddOns = 'LIST_DEV_BOX_ADD_ONS',
    ListDevBoxAddOnsError = 'LIST_DEV_BOX_ADD_ONS_ERROR',
    ListDevBoxAddOnsFailed = 'LIST_DEV_BOX_ADD_ONS_FAILED',
    ListDevBoxAddOnsSuccess = 'LIST_DEV_BOX_ADD_ONS_SUCCESS',
    LoadDevBoxAddOnsForDevBoxes = 'LOAD_DEV_BOX_ADD_ONS_FOR_DEV_BOXES',
    LoadDevBoxAddOnsForDevBoxesError = 'LOAD_DEV_BOX_ADD_ONS_FOR_DEV_BOXES_ERROR',
    LoadDevBoxAddOnsForDevBoxesFailed = 'LOAD_DEV_BOX_ADD_ONS_FOR_DEV_BOXES_FAILED',
    LoadDevBoxAddOnsForDevBoxesSuccess = 'LOAD_DEV_BOX_ADD_ONS_FOR_DEV_BOXES_SUCCESS',
    PollNonTerminalDevBoxAddOn = 'POLL_NON_TERMINAL_DEV_BOX_ADD_ON',
    PollNonTerminalDevBoxAddOnComplete = 'POLL_NON_TERMINAL_DEV_BOX_ADD_ON_COMPLETE',
    PollNonTerminalDevBoxAddOnError = 'POLL_NON_TERMINAL_DEV_BOX_ADD_ON_ERROR',
    GetDevBoxAddOn = 'GET_DEV_BOX_ADD_ON',
    GetDevBoxAddOnError = 'GET_DEV_BOX_ADD_ON_ERROR',
    GetDevBoxAddOnFailed = 'GET_DEV_BOX_ADD_ON_FAILED',
    GetDevBoxAddOnSuccess = 'GET_DEV_BOX_ADD_ON_SUCCESS',
    EnableDevBoxAddOn = 'ENABLE_DEV_BOX_ADD_ON',
    EnableDevBoxAddOnAccepted = 'ENABLE_DEV_BOX_ADD_ON_ACCEPTED',
    EnableDevBoxAddOnError = 'ENABLE_DEV_BOX_ADD_ON_ERROR',
    EnableDevBoxAddOnFailed = 'ENABLE_DEV_BOX_ADD_ON_FAILED',
    EnableDevBoxAddOnSuccess = 'ENABLE_DEV_BOX_ADD_ON_SUCCESS',
    EnableDevBoxAddOnWithPolling = 'ENABLE_DEV_BOX_ADD_ON_WITH_POLLING',
    EnableDevBoxAddOnWithPollingError = 'ENABLE_DEV_BOX_ADD_ON_WITH_POLLING_ERROR',
    EnableDevBoxAddOnWithPollingFailed = 'ENABLE_DEV_BOX_ADD_ON_WITH_POLLING_FAILED',
    EnableDevBoxAddOnWithPollingSuccess = 'ENABLE_DEV_BOX_ADD_ON_WITH_POLLING_SUCCESS',
    CreateOrReplaceDevBoxAddOn = 'CREATE_OR_REPLACE_DEV_BOX_ADD_ON',
    CreateOrReplaceDevBoxAddOnError = 'CREATE_OR_REPLACE_DEV_BOX_ADD_ON_ERROR',
    CreateOrReplaceDevBoxAddOnFailed = 'CREATE_OR_REPLACE_DEV_BOX_ADD_ON_FAILED',
    CreateOrReplaceDevBoxAddOnSuccess = 'CREATE_OR_REPLACE_DEV_BOX_ADD_ON_SUCCESS',
    EnableDevBoxTunnel = 'ENABLE_DEV_BOX_TUNNEL',
    EnableDevBoxTunnelError = 'ENABLE_DEV_BOX_TUNNEL_ERROR',
    EnableDevBoxTunnelFailed = 'ENABLE_DEV_BOX_TUNNEL_FAILED',
    EnableDevBoxTunnelSuccess = 'ENABLE_DEV_BOX_TUNNEL_SUCCESS',
    EnableDevBoxTunnelPhaseStartDevBox = 'ENABLE_DEV_BOX_TUNNEL_PHASE_START_DEV_BOX',
    EnableDevBoxTunnelPhaseTunnelEnabling = 'ENABLE_DEV_BOX_TUNNEL_PHASE_TUNNEL_ENABLING',
    EnableDevBoxTunnelPhaseTunnelEnablingFinished = 'ENABLE_DEV_BOX_TUNNEL_PHASE_TUNNEL_ENABLING_FINISHED',
    EnableDevBoxTunnelPhaseTunnelNotEnabled = 'ENABLE_DEV_BOX_TUNNEL_PHASE_TUNNEL_NOT_ENABLED',
}

export type DevBoxAddOnsActionTypeType =
    | ActionType<'POLL_NON_TERMINAL_DEV_BOX_ADD_ON', '_COMPLETE' | '_ERROR'>
    | ActionType<'LIST_DEV_BOX_ADD_ONS', '_ERROR' | '_FAILED' | '_SUCCESS'>
    | ActionType<'LOAD_DEV_BOX_ADD_ONS_FOR_DEV_BOXES', '_ERROR' | '_FAILED' | '_SUCCESS'>
    | ActionType<'GET_DEV_BOX_ADD_ON', '_ERROR' | '_FAILED' | '_SUCCESS'>
    | ActionType<'ENABLE_DEV_BOX_ADD_ON', '_ACCEPTED' | '_ERROR' | '_FAILED' | '_SUCCESS'>
    | ActionType<'ENABLE_DEV_BOX_ADD_ON_WITH_POLLING', '_ERROR' | '_FAILED' | '_SUCCESS'>
    | ActionType<'CREATE_OR_REPLACE_DEV_BOX_ADD_ON', '_ERROR' | '_FAILED' | '_SUCCESS'>
    | ActionType<'ENABLE_DEV_BOX_TUNNEL', '_ERROR' | '_FAILED' | '_SUCCESS'>
    | ActionType<'ENABLE_DEV_BOX_TUNNEL_PHASE_START_DEV_BOX', never, false>
    | ActionType<'ENABLE_DEV_BOX_TUNNEL_PHASE_TUNNEL_ENABLING', never, false>
    | ActionType<'ENABLE_DEV_BOX_TUNNEL_PHASE_TUNNEL_ENABLING_FINISHED', never, false>;

export const DevBoxAddOnsActionTypeMap: UnionValueMap<DevBoxAddOnsActionName, DevBoxAddOnsActionTypeType> = {
    PollNonTerminalDevBoxAddOnComplete: 'POLL_NON_TERMINAL_DEV_BOX_ADD_ON_COMPLETE',
    PollNonTerminalDevBoxAddOnError: 'POLL_NON_TERMINAL_DEV_BOX_ADD_ON_ERROR',
    PollNonTerminalDevBoxAddOn: 'POLL_NON_TERMINAL_DEV_BOX_ADD_ON',
    ListDevBoxAddOnsError: 'LIST_DEV_BOX_ADD_ONS_ERROR',
    ListDevBoxAddOnsFailed: 'LIST_DEV_BOX_ADD_ONS_FAILED',
    ListDevBoxAddOnsSuccess: 'LIST_DEV_BOX_ADD_ONS_SUCCESS',
    ListDevBoxAddOns: 'LIST_DEV_BOX_ADD_ONS',
    LoadDevBoxAddOnsForDevBoxes: 'LOAD_DEV_BOX_ADD_ONS_FOR_DEV_BOXES',
    LoadDevBoxAddOnsForDevBoxesError: 'LOAD_DEV_BOX_ADD_ONS_FOR_DEV_BOXES_ERROR',
    LoadDevBoxAddOnsForDevBoxesFailed: 'LOAD_DEV_BOX_ADD_ONS_FOR_DEV_BOXES_FAILED',
    LoadDevBoxAddOnsForDevBoxesSuccess: 'LOAD_DEV_BOX_ADD_ONS_FOR_DEV_BOXES_SUCCESS',
    GetDevBoxAddOn: 'GET_DEV_BOX_ADD_ON',
    GetDevBoxAddOnError: 'GET_DEV_BOX_ADD_ON_ERROR',
    GetDevBoxAddOnFailed: 'GET_DEV_BOX_ADD_ON_FAILED',
    GetDevBoxAddOnSuccess: 'GET_DEV_BOX_ADD_ON_SUCCESS',
    EnableDevBoxAddOn: 'ENABLE_DEV_BOX_ADD_ON',
    EnableDevBoxAddOnAccepted: 'ENABLE_DEV_BOX_ADD_ON_ACCEPTED',
    EnableDevBoxAddOnError: 'ENABLE_DEV_BOX_ADD_ON_ERROR',
    EnableDevBoxAddOnFailed: 'ENABLE_DEV_BOX_ADD_ON_FAILED',
    EnableDevBoxAddOnSuccess: 'ENABLE_DEV_BOX_ADD_ON_SUCCESS',
    EnableDevBoxAddOnWithPolling: 'ENABLE_DEV_BOX_ADD_ON_WITH_POLLING',
    EnableDevBoxAddOnWithPollingError: 'ENABLE_DEV_BOX_ADD_ON_WITH_POLLING_ERROR',
    EnableDevBoxAddOnWithPollingFailed: 'ENABLE_DEV_BOX_ADD_ON_WITH_POLLING_FAILED',
    EnableDevBoxAddOnWithPollingSuccess: 'ENABLE_DEV_BOX_ADD_ON_WITH_POLLING_SUCCESS',
    CreateOrReplaceDevBoxAddOn: 'CREATE_OR_REPLACE_DEV_BOX_ADD_ON',
    CreateOrReplaceDevBoxAddOnError: 'CREATE_OR_REPLACE_DEV_BOX_ADD_ON_ERROR',
    CreateOrReplaceDevBoxAddOnFailed: 'CREATE_OR_REPLACE_DEV_BOX_ADD_ON_FAILED',
    CreateOrReplaceDevBoxAddOnSuccess: 'CREATE_OR_REPLACE_DEV_BOX_ADD_ON_SUCCESS',
    EnableDevBoxTunnel: 'ENABLE_DEV_BOX_TUNNEL',
    EnableDevBoxTunnelError: 'ENABLE_DEV_BOX_TUNNEL_ERROR',
    EnableDevBoxTunnelFailed: 'ENABLE_DEV_BOX_TUNNEL_FAILED',
    EnableDevBoxTunnelSuccess: 'ENABLE_DEV_BOX_TUNNEL_SUCCESS',
};

/**
 * Payloads
 */

export interface GetDevBoxAddOnPayload extends IndexedPayload {
    name: string;
}

export interface EnableDevBoxAddOnPayload extends IndexedPayload {
    name: string;
}

export interface EnableDevBoxAddOnAcceptedPayload extends IndexedPayload {
    name: string;
    result: EnableDevBoxAddOnContract; // Will hold the response data from the enable API call
}

export interface EnableDevBoxAddOnWithPollingPayload extends IndexedPayload {
    name: string;
}

export interface CreateOrReplaceDevBoxAddOnPayload extends IndexedPayload {
    name: string;
    kind: string;
    hostingResourceName?: string;
}

export type PollNonTerminalDevBoxAddOnCompletePayload = IndexedPayload & OptionalResultPayload<DevBoxAddOn>;

export type ListAddOnsSuccessPayload = IndexedPayload & ResultPayload<DevBoxAddOn[]>;

export interface EnableTunnelPayload extends IndexedPayload {
    tunnelName?: string;
}
export type EnableDevBoxTunnelErrorPayload = ErrorPayload & IndexedPayload;
export type EnableDevBoxTunnelFailedPayload = FailedPayload & IndexedPayload;
export type EnableDevBoxTunnelSuccessPayload = IndexedPayload;

/**
 * Action shorthands
 */

export type ListDevBoxAddOnsAction = ReturnType<typeof listDevBoxAddOns>;
export type LoadDevBoxAddOnsForDevBoxesAction = ReturnType<typeof loadDevBoxAddOnsForDevBoxes>;
export type PollNonTerminalDevBoxAddOnAction = ReturnType<typeof pollNonTerminalDevBoxAddOn>;
export type GetDevBoxAddOnAction = ReturnType<typeof getDevBoxAddOn>;
export type EnableDevBoxAddOnAction = ReturnType<typeof enableDevBoxAddOn>;
export type EnableDevBoxAddOnAcceptedAction = ReturnType<typeof enableDevBoxAddOnAccepted>;
export type EnableDevBoxAddOnWithPollingAction = ReturnType<typeof enableDevBoxAddOnWithPolling>;
export type CreateOrReplaceDevBoxAddOnAction = ReturnType<typeof createOrReplaceDevBoxAddOn>;
export type EnableTunnelAction = ReturnType<typeof enableDevBoxTunnel>;
export type EnableDevBoxTunnelErrorAction = ReturnType<typeof enableDevBoxTunnelError>;
export type EnableDevBoxTunnelFailedAction = ReturnType<typeof enableDevBoxTunnelFailed>;
export type EnableDevBoxTunnelSuccessAction = ReturnType<typeof enableDevBoxTunnelSuccess>;
export type EnableDevBoxTunnelPhaseStartDevBoxAction = ReturnType<typeof enableDevBoxTunnelPhaseStartDevBox>;
export type EnableDevBoxTunnelPhaseTunnelEnablingAction = ReturnType<typeof enableDevBoxTunnelPhaseTunnelEnabling>;
export type EnableDevBoxTunnelPhaseTunnelEnablingFinishedAction = ReturnType<
    typeof enableDevBoxTunnelPhaseTunnelEnablingFinished
>;
export type EnableDevBoxTunnelPhaseTunnelNotEnabledAction = ReturnType<typeof enableDevBoxTunnelPhaseTunnelNotEnabled>;
